// analyticsUI.js - Refactored for Content Generation v2
import {
  getPlayerBaseline,
  getPlayerLevelBests,
  calculatePlayerImprovement,
  getRecentSessionStats,
  getRecentLevelPerformances
} from "../services/analyticsService.js";
// Removed UIController import, this file doesn't show/hide overlays

/**
* Populates the analytics overlay content container.
* Assumes the overlay structure (#analytics-overlay > .overlay-content) exists in index.html.
* @param {string} userId - The user's ID
* @param {string} gameType - The game type (e.g., "PPTHero" or "Shortcut")
*/
export async function populateAnalyticsOverlay(userId, gameType = "PPTHero") {
    // Find the specific content div for analytics
    const contentDiv = document.querySelector("#analytics-overlay .overlay-content");
    if (!contentDiv) {
        console.error("Analytics overlay content container not found!");
        return; // Exit if the container isn't in the DOM
    }

    // --- Clear previous content ---
    // Find the close button to preserve it
    const closeBtn = contentDiv.querySelector('.close-btn');
    contentDiv.innerHTML = ''; // Clear everything inside
    if (closeBtn) contentDiv.appendChild(closeBtn); // Re-add the close button

    // --- Add Standard Header ---
    const title = document.createElement("h2");
    title.textContent = "Performance Analytics"; // Standard title
    contentDiv.appendChild(title);

    // --- Loading Indicator ---
    const loadingDiv = document.createElement("div");
    loadingDiv.className = "loading-indicator"; // Use CSS class
    loadingDiv.textContent = "Loading performance data...";
    contentDiv.appendChild(loadingDiv);

    try {
        console.log(`Populating analytics for user ${userId}, game type ${gameType}`);

        // --- Fetch Data ---
        // Fetch all data concurrently for speed
        const [baseline, levelBests, improvement, recentSessionStats, recentPerformances] = await Promise.all([
            getPlayerBaseline(userId).catch(e => { console.error("Failed baseline:", e); return null; }),
            getPlayerLevelBests(userId, gameType).catch(e => { console.error("Failed level bests:", e); return []; }),
            calculatePlayerImprovement(userId, gameType).catch(e => { console.error("Failed improvement:", e); return null; }),
            getRecentSessionStats(userId, gameType).catch(e => { console.error("Failed recent stats:", e); return null; }),
            getRecentLevelPerformances(userId, gameType).catch(e => { console.error("Failed recent perfs:", e); return []; })
        ]);

        // --- Remove Loading Indicator ---
        contentDiv.removeChild(loadingDiv);

        // --- Create Tabs UI ---
        const tabsContainer = document.createElement("div");
        tabsContainer.className = "analytics-tabs"; // Use class from CSS

        const summaryTab = createTabButton("Summary", true); // Default active tab
        const progressTab = createTabButton("Progress");
        const levelsTab = createTabButton("Level Details");

        tabsContainer.appendChild(summaryTab);
        tabsContainer.appendChild(progressTab);
        tabsContainer.appendChild(levelsTab);
        contentDiv.appendChild(tabsContainer);

        // --- Create Content Areas ---
        const summaryContent = createTabContentArea(true); // Default visible content
        const progressContent = createTabContentArea();
        const levelsContent = createTabContentArea();

        contentDiv.appendChild(summaryContent);
        contentDiv.appendChild(progressContent);
        contentDiv.appendChild(levelsContent);

        // --- Tab Click Handlers ---
        const tabs = [summaryTab, progressTab, levelsTab];
        const contents = [summaryContent, progressContent, levelsContent];

        tabs.forEach((tab, index) => {
            tab.addEventListener("click", () => {
                // Update tab appearance
                tabs.forEach(t => t.classList.remove("active"));
                tab.classList.add("active");
                // Update content visibility
                contents.forEach(c => c.classList.remove("show"));
                contents[index].classList.add("show");
            });
        });

        // --- Populate Content Areas ---
        populateSummaryTab(summaryContent, baseline, improvement, recentSessionStats);
        populateProgressTab(progressContent, baseline, improvement);
        populateLevelsTab(levelsContent, levelBests, recentPerformances, userId, gameType); // Populate levels tab

    } catch (error) {
        console.error("Error loading analytics:", error);
        // Ensure loading indicator is removed on error too
        if (contentDiv.contains(loadingDiv)) {
            contentDiv.removeChild(loadingDiv);
        }
        // Display a user-friendly error message
        const errorMsg = createMessageDiv(`Could not load performance data. Please try again later. (${error.message})`, true);
        contentDiv.appendChild(errorMsg);
    }
}

// --- Helper Functions for Building UI ---

function createTabButton(text, isActive = false) {
    const button = document.createElement("div");
    button.className = "analytics-tab" + (isActive ? " active" : ""); // Use class from CSS
    button.textContent = text;
    // Add ARIA roles for accessibility
    button.setAttribute('role', 'tab');
    button.setAttribute('aria-selected', isActive.toString());
    button.setAttribute('tabindex', isActive ? '0' : '-1'); // Manage focus
    return button;
}

function createTabContentArea(isActive = false) {
    const div = document.createElement("div");
    div.className = "tab-content" + (isActive ? " show" : ""); // Use class from CSS
    // Add ARIA roles for accessibility
    div.setAttribute('role', 'tabpanel');
    div.hidden = !isActive;
    return div;
}

function populateSummaryTab(container, baseline, improvement, recentSessionStats) {
    // Clear previous content
    container.innerHTML = '';

    // Check if baseline data is valid and exists
    if (baseline && baseline.createdAt && typeof baseline.score === 'number') {
        const baselineDate = baseline.createdAt?.toDate ? new Date(baseline.createdAt.toDate()).toLocaleDateString() : "Unknown";
        const baselineSection = document.createElement('div');
        baselineSection.innerHTML = `<h3>Your Baseline (Established ${baselineDate})</h3>`;
        baselineSection.appendChild(createStatsGrid([
            { label: "Base Score", value: Math.round(baseline.score) }, // Default to 0 if null/undefined
            { label: "Accuracy", value: `${Math.round(baseline.averageAccuracy || 0)}%` },
            { label: "Avg Time", value: `${Math.round(baseline.averageCompletionTime || 0)}s` },
            { label: "Shortcuts", value: baseline.successfulShortcuts || 0 }
        ]));
        container.appendChild(baselineSection);

        // Display Recent Session stats if available
        if (recentSessionStats) {
            const recentSection = document.createElement('div');
            recentSection.innerHTML = `<h3>Most Recent Session</h3>`;
            recentSection.appendChild(createImprovementGrid([
                { name: "Score", value: recentSessionStats.scoreChange || 0 },
                { name: "Accuracy", value: recentSessionStats.accuracyChange || 0 },
                { name: "Speed", value: recentSessionStats.timeChange || 0 }, // Note: Higher % is better speed
                { name: "Shortcuts", value: recentSessionStats.shortcutsChange || 0 }
            ]));
            container.appendChild(recentSection);
        }

        // Display Overall Improvement if available
        if (improvement) {
            const improvementSection = document.createElement('div');
            improvementSection.innerHTML = `<h3>Overall Improvement Trend</h3>`;
            improvementSection.appendChild(createImprovementGrid([
                { name: "Score", value: improvement.scoreImprovement || 0 },
                { name: "Accuracy", value: improvement.accuracyImprovement || 0 },
                { name: "Speed", value: improvement.timeImprovement || 0 },
                { name: "Shortcuts", value: improvement.shortcutsImprovement || 0 }
            ]));
            container.appendChild(improvementSection);
        } else if (recentSessionStats) { // If we have recent but not overall
             container.appendChild(createMessageDiv("Keep playing to track long-term improvement!"));
        }

    } else {
        // Message if no baseline data exists
        container.appendChild(createMessageDiv("Complete the calibration round (first 10 levels) to establish your baseline performance."));
    }
}

function populateProgressTab(container, baseline, improvement) {
    // Clear previous content
     container.innerHTML = '';

     // Check if necessary data is available
     if (!baseline || !baseline.createdAt || !improvement) {
         container.appendChild(createMessageDiv("Complete calibration and play more levels to see your progress visualized here."));
         return;
     }
     container.innerHTML = `<h3>Progress vs Baseline</h3>`;

     // Extract baseline values safely
     const baselineScore = baseline.score || 0;
     const baselineAccuracy = baseline.averageAccuracy || 0;
     const baselineTime = baseline.averageCompletionTime || 0;

     // Extract improvement percentages safely
     const scoreImprovement = improvement.scoreImprovement || 0;
     const accuracyImprovement = improvement.accuracyImprovement || 0;
     const timeImprovement = improvement.timeImprovement || 0;

     // Calculate current estimated values
     const currentScore = Math.round(baselineScore * (1 + scoreImprovement / 100));
     // Clamp accuracy between 0 and 100
     const currentAccuracy = Math.max(0, Math.min(100, Math.round(baselineAccuracy * (1 + accuracyImprovement / 100))));
     // Ensure time doesn't go below zero
     const currentTime = Math.max(0, Math.round(baselineTime * (1 - timeImprovement / 100)));

     // Create Progress Bars using the helper
     container.appendChild(createProgressBar("Score", baselineScore, currentScore, '', scoreImprovement));
     container.appendChild(createProgressBar("Accuracy", baselineAccuracy, currentAccuracy, '%', accuracyImprovement));
     container.appendChild(createProgressBar("Avg Time", baselineTime, currentTime, 's', timeImprovement, true)); // invert=true for time
}


function populateLevelsTab(container, levelBests, recentPerformances) {
     // Clear previous content
     container.innerHTML = '';

    if (!levelBests || levelBests.length === 0) {
        container.appendChild(createMessageDiv("Play some levels to see detailed performance stats here."));
        return;
    }
    container.innerHTML = `<h3>Level Performance Details</h3>`;

    // Sort valid bests by level number
    const validLevelBests = levelBests
        .filter(level => level && typeof level.levelNumber === 'number' && level.levelNumber > 0)
        .sort((a, b) => a.levelNumber - b.levelNumber);

    if (validLevelBests.length === 0) {
         container.appendChild(createMessageDiv("No valid level data found."));
         return;
    }

    const table = document.createElement("table");
    // More descriptive headers
    table.innerHTML = `
        <thead>
            <tr>
                <th>Level</th>
                <th>Best Score</th>
                <th>Best Time (s)</th>
                <th>Best Accuracy (%)</th>
                <th>Max Shortcuts</th>
                <th>Recent Change vs Best</th>
            </tr>
        </thead>
        <tbody></tbody>
    `;
    const tbody = table.querySelector('tbody');

    validLevelBests.forEach(best => {
        // Find the most recent performance for this specific level
        const recent = recentPerformances
            .filter(p => p.levelNumber === best.levelNumber)
            .sort((a, b) => (b.timestamp?.toDate ? b.timestamp.toDate() : 0) - (a.timestamp?.toDate ? a.timestamp.toDate() : 0))[0]; // Get latest

        const row = document.createElement('tr');

        // Calculate change HTML only if recent data exists
        const scoreChangeHtml = recent ? calculateChangeHtml(recent.score, best.score) : '';
        const timeChangeHtml = recent ? calculateTimeChangeHtml(recent.completionTime, best.completionTime) : '';
        const accChangeHtml = recent ? calculateChangeHtml(recent.accuracy, best.accuracy) : '';
        const scChangeHtml = recent ? calculateChangeHtml(recent.successfulShortcuts, best.successfulShortcuts) : '';

        // Combine changes into one cell, using small text and line breaks
        const changes = [
            scoreChangeHtml ? `S: ${scoreChangeHtml}` : '',
            timeChangeHtml ? `T: ${timeChangeHtml}` : '',
            accChangeHtml ? `A: ${accChangeHtml}` : '',
            scChangeHtml ? `SC: ${scChangeHtml}` : ''
        ].filter(c => c).join('<br>');

        row.innerHTML = `
            <td>${best.levelNumber}</td>
            <td>${Math.round(best.score || 0)}</td>
            <td>${Math.round(best.completionTime || 0)}</td>
            <td>${Math.round(best.accuracy || 0)}</td>
            <td>${best.successfulShortcuts || 0}</td>
            <td style="font-size: var(--font-size-xs); line-height: 1.3;">${changes || '-'}</td>
        `;
        tbody.appendChild(row);
    });

    container.appendChild(table);
    // Refined explanation text
    container.appendChild(createMessageDiv("Recent Change shows your last attempt's performance change vs your best for that level (S=Score, T=Time, A=Accuracy, SC=Shortcuts).", false, 'var(--font-size-xs)'));
}

// --- UI Element Creation Helpers ---

function createStatsGrid(stats) {
    const grid = document.createElement('div');
    grid.className = 'stat-grid';
    stats.forEach(stat => {
        grid.innerHTML += `
            <div class="stat-box">
                <div class="stat-value">${stat.value !== undefined && stat.value !== null ? stat.value : '-'}</div>
                <div class="stat-label">${stat.label}</div>
            </div>`;
    });
    return grid;
}

function createImprovementGrid(improvements) {
    const grid = document.createElement('div');
    grid.className = 'improvement-grid';
    improvements.forEach(item => {
        const value = item.value; // Allow null/undefined
        let displayValue = '-';
        let valueClass = '';
        if (typeof value === 'number' && !isNaN(value)) {
             const isPositive = value >= 0;
             valueClass = isPositive ? 'positive' : 'negative';
             displayValue = `${isPositive ? '+' : ''}${Math.round(value)}%`;
        }

        grid.innerHTML += `
            <div class="improvement-box">
                <div class="improvement-value ${valueClass}">${displayValue}</div>
                <div class="improvement-label">${item.name}</div>
            </div>`;
    });
    return grid;
}

function createProgressBar(label, baselineValue, currentValue, unit = '', improvementPercent, invert = false) {
    const container = document.createElement('div');
    container.className = 'progress-bar-container';

    // Handle zero baseline for percentage calculation
    let currentPercentOfBaseline = 100;
    if (baselineValue > 0) {
        currentPercentOfBaseline = (currentValue / baselineValue) * 100;
    } else if (currentValue > 0) {
        currentPercentOfBaseline = 200; // Show a full bar if baseline was 0 but current is positive
    } else {
        currentPercentOfBaseline = 0; // Both are zero
    }

    // For time, we want the bar to represent 100% - percentage (lower is better)
    // The visual length decreases as time improves (currentValue gets smaller)
    const displayWidthPercent = invert
        ? Math.max(0, Math.min(100, currentPercentOfBaseline)) // Width decreases as value decreases
        : Math.max(0, currentPercentOfBaseline); // Width increases as value increases

    // Cap visual width to prevent extreme bars, e.g., max 200%
    const cappedDisplayWidth = Math.min(200, displayWidthPercent);

    // Determine color based on improvement percentage
    let improvementColorClass = '';
    let improvementText = '-';
    if (typeof improvementPercent === 'number' && !isNaN(improvementPercent)) {
         improvementColorClass = improvementPercent >= 0 ? 'positive' : 'negative';
         improvementText = `(${(improvementPercent >= 0 ? '+' : '') + Math.round(improvementPercent)}%)`;
    }

    container.innerHTML = `
        <div class="progress-bar-label">${label}</div>
        <div class="progress-bar">
            <!-- Baseline visualization (always 100% width conceptually) -->
            <div class="progress-bar-fill baseline" style="width: 100%;"></div>
             <!-- Current value overlay -->
             <div class="progress-bar-fill current ${improvementColorClass}" style="width: ${cappedDisplayWidth}%;"></div>
        </div>
        <div class="progress-bar-value">${currentValue}${unit}</div>
        <div class="progress-bar-change ${improvementColorClass}">
             ${improvementText}
         </div>
    `;
    return container;
}


function createMessageDiv(text, isError = false, fontSize = 'var(--font-size-sm)') {
    const div = document.createElement('div');
    // Use classes for styling consistency
    div.className = `message-area ${isError ? 'error-message' : 'info-message'}`;
    div.textContent = text;
    div.style.fontSize = fontSize; // Allow overriding font size
    div.style.display = 'block'; // Ensure it's visible when created
    div.style.marginTop = 'var(--space-md)'; // Add margin
    return div;
}

// --- Calculation Helper Functions ---

// Calculate standard percentage change
function calculateChangeHtml(current, best) {
    if (typeof current !== 'number' || typeof best !== 'number' || isNaN(current) || isNaN(best)) return ''; // Added NaN checks
    // Avoid division by zero; if best is 0, show current value or indicate infinite change if current is non-zero
    if (best === 0) {
        return current === 0 ? '0%' : (current > 0 ? '+INF%' : '-INF%');
    }
    const change = ((current - best) / Math.abs(best)) * 100;
    if (isNaN(change)) return ''; // Handle potential NaN result
    const colorClass = change >= 0 ? 'positive' : 'negative';
    return `<span class="${colorClass}">${change >= 0 ? '+' : ''}${Math.round(change)}%</span>`;
}

// Calculate percentage change for time (lower is better)
function calculateTimeChangeHtml(current, best) {
    if (typeof current !== 'number' || typeof best !== 'number' || isNaN(current) || isNaN(best)) return ''; // Added NaN checks
    // Avoid division by zero
    if (best === 0) {
        return current === 0 ? '0%' : (current > 0 ? '-INF%' : '+INF%'); // If current > 0, time got worse infinitely
    }
    // Improvement % = (Old - New) / Old * 100
    const change = ((best - current) / best) * 100;
     if (isNaN(change)) return ''; // Handle potential NaN result
    const colorClass = change >= 0 ? 'positive' : 'negative'; // Positive change means faster (better)
    return `<span class="${colorClass}">${change >= 0 ? '+' : ''}${Math.round(change)}%</span>`;
}